import QuestionCriterionSelect from 'components/CreateQuiz/QuestionCriterionSelect';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, IconButton, RichTextArea, Stack, styled, Text } from 'shell/components';

const QuestionSettingsCard = forwardRef(
    (
        {
            question,
            questionNumber,
            showQuestionNumber,
            isMobile,
            isQuestionRubric,
            criteria,
            mappedCriterionIds,
            disabled = false,
            onCriterionIdsChange,
            onGradingNotesChange,
        },
        ref
    ) => {
        const { t } = useTranslation('quiz');

        return (
            <QuestionCard
                ref={ref}
                id={`quiz-question-settings-${question.id}`}
                component={'article'}
                tabIndex={-1}
                spacing={5}
            >
                <Stack
                    direction={isMobile ? 'column' : 'row'}
                    alignItems={'flex-start'}
                    justifyContent={'space-between'}
                    spacing={3}
                >
                    <QuestionTitle direction={'row'} alignItems={'flex-start'} spacing={1}>
                        {showQuestionNumber && (
                            <QuestionNumber component={'span'} variant={'p1'} weight={'medium'}>
                                {t('create.question-number-short', 'Q{{number}})', { number: questionNumber })}
                            </QuestionNumber>
                        )}
                        <QuestionText component={'h3'} variant={'p1'} weight={'medium'} rich={question.rich}>
                            {question.content || t('create.question-missing-content', 'Question text is unavailable.')}
                        </QuestionText>
                    </QuestionTitle>
                    <PointsPill weight={'medium'}>
                        {t('create.points', 'Points: {{points}}', { points: question.points })}
                    </PointsPill>
                </Stack>
                <Divider />
                {isQuestionRubric && (
                    <QuestionCriterionSelect
                        questionId={question.id}
                        questionNumber={questionNumber}
                        criteria={criteria}
                        mappedCriterionIds={mappedCriterionIds}
                        selectedIds={question.criterionIds}
                        disabled={disabled}
                        onChange={onCriterionIdsChange}
                    />
                )}
                <Stack spacing={1.25}>
                    <Stack direction={'row'} alignItems={'center'} spacing={2}>
                        <Text id={`quiz-question-${question.id}-notes-label`} weight={'medium'}>
                            {t('create.grading-notes', 'Grading Notes')}
                        </Text>
                        <IconButton
                            kind={'info'}
                            size={'xsmall'}
                            variant={'circle'}
                            aria-label={t('create.grading-notes-help', 'About grading notes')}
                            tooltip={{
                                component: t(
                                    'create.grading-notes-tooltip',
                                    'Add guidance that helps the AI evaluate this question consistently.'
                                ),
                                maxWidth: '17.5rem',
                            }}
                        />
                    </Stack>
                    <GradingNotesInput
                        aria-label={t('create.grading-notes-for-question', 'Grading notes for question {{number}}', {
                            number: questionNumber,
                        })}
                        aria-describedby={`quiz-question-${question.id}-notes-label`}
                        placeholder={t(
                            'create.grading-notes-placeholder',
                            "Please include detailed grading notes that support AI-assisted evaluation. If a rubric isn't provided, sharing the point scales for grading is essential, along with clear criteria on how students can earn or lose points."
                        )}
                        value={question.gradingNotes}
                        disabled={disabled}
                        onChange={onGradingNotesChange}
                    />
                </Stack>
            </QuestionCard>
        );
    }
);

QuestionSettingsCard.displayName = 'QuestionSettingsCard';

const QuestionCard = styled(Stack)(({ theme }) => ({
    scrollMarginTop: theme.spacing(20),
    padding: theme.spacing(5),
    border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    borderRadius: '16px',
    background: theme.palette.ensightful.shades[0],
    '&:focus': { outline: 'none' },
    '&:focus-visible': {
        outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
        outlineOffset: '2px',
    },
}));

const QuestionTitle = styled(Stack)(() => ({
    flex: 1,
    minWidth: 0,
}));

const QuestionNumber = styled(Text)(() => ({ flexShrink: 0 }));

const QuestionText = styled(Text)(() => ({
    flex: 1,
    minWidth: 0,
    '& p': { margin: 0 },
    '& img, & video, & iframe': { maxWidth: '100%' },
    '& table': { display: 'block', maxWidth: '100%', overflowX: 'auto' },
}));

const PointsPill = styled(Text)(({ theme }) => ({
    flexShrink: 0,
    padding: theme.spacing(1, 2),
    borderRadius: '40px',
    background: theme.palette.ensightful.primary[50],
    color: theme.palette.ensightful.primary[700],
    whiteSpace: 'nowrap',
}));

const GradingNotesInput = styled(RichTextArea)(() => ({
    '& .MuiRichTextArea-root': {
        minHeight: '170px',
    },
}));

export default QuestionSettingsCard;
